// Theme configuration for the digital menu app
// Supports dynamic theming based on store details

// Common shared colors for performance
const COMMON = {
  white: '#ffffff',
  black: '#000000',
  transparent: 'transparent',
  gray200: '#e5e7eb',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray900: '#111827',
  graySurface: '#f9fafb',
  vegGreen: '#16a34a',
  nonVegRed: '#dc2626',
};

// Cherry Theme - Based on current pink/red color scheme (Original colors)
export const cherryTheme = {
  name: 'cherry',
  displayName: 'Cherry',
  colors: {
    // Primary button colors
    buttonGradientFrom: '#ef4444', // red-500
    buttonGradientTo: '#ec4899',   // pink-500
    buttonText: COMMON.white,
    buttonRing: '#fca5a5',         // red-300
    
    // Highlighted/Selected states
    highlightedBg: '#fce7f3',      // pink-100
    highlightedBgGradientFrom: '#fce7f3', // pink-100
    highlightedBgGradientTo: '#fdf2f8',   // pink-50
    highlightedText: '#f91944',    // Original primary color
    highlightedRipple: '#fbcfe8',  // pink-200
    
    // Cart badge
    cartBadgeGradientFrom: '#ef4444', // red-500
    cartBadgeGradientTo: '#dc2626',   // red-600
    cartBadgeText: COMMON.white,
    
    // Bottom tab navigation
    bottomTabBg: COMMON.white,
    bottomTabBorder: COMMON.gray200,
    bottomTabInactiveText: COMMON.gray600,
    bottomTabActiveText: '#f91944',   // Original primary color
    bottomTabActiveBgFrom: '#fce7f3', // pink-100
    bottomTabActiveBgTo: '#fdf2f8',   // pink-50
    
    // Quantity controls
    quantityControlBg: '#f91944',  // Original primary color
    quantityControlText: COMMON.white,
    
    // General UI
    primaryColor: '#f91944',       // Original primary color
    secondaryColor: '#ef4444',     // red-500
    accentColor: '#f91944',        // Original primary color
    backgroundColor: COMMON.white,
    surfaceColor: COMMON.graySurface,
    borderColor: COMMON.gray200,
    textPrimary: COMMON.gray900,
    textSecondary: COMMON.gray500,
    
    // Hover states
    hoverColor: '#f91944',         // Original primary color
    
    // Offer Tags & Badges
    offerPriceText: COMMON.vegGreen,
    badgeVeg: COMMON.vegGreen,
    badgeNonVeg: COMMON.nonVegRed,
    seasonalBadgeFrom: '#f97316',  // orange-500
    seasonalBadgeTo: '#dc2626',    // red-600
    featuredBadgeFrom: '#fde047',  // yellow-300
    featuredBadgeTo: '#eab308',    // yellow-500
    featuredBadgeText: '#713f12',  // yellow-900
    offerHeaderBg: '#f97316',      // orange-500
    offerHeaderText: COMMON.white,
    
    // Search & Product Buttons
    searchButtonBg: '#f91944',     // Original primary color
    searchButtonText: COMMON.white,
    searchButtonBorder: COMMON.transparent,
    searchIconColor: '#f91944',    // Original primary color for search icon
    searchTextColor: '#f91944',    // Original primary color for search text
    productAddButtonBg: '#f91944', // Original primary color
    productAddButtonText: COMMON.white,
    productAddButtonBorder: COMMON.transparent,
    
    // Floating Cart Icon
    cartIconGradientFrom: '#ef4444', // red-500
    cartIconGradientVia: '#ec4899',  // pink-500
    cartIconGradientTo: '#facc15',   // yellow-400
    cartIconText: COMMON.white,
    cartIconBorder: COMMON.white,
    cartIconShadow: '0 4px 32px 0 rgba(249,25,68,0.18)',
    cartIconBadgeBg: '#facc15',      // yellow-400
    cartIconBadgeText: '#1f2937',    // gray-800
    
    // Top Navigation Menu Options
    navOffersIconActive: '#c2410c',  // orange-700
    navOffersIconInactive: '#ea580c', // orange-600
    navOffersBgActive: '#fed7aa',    // orange-200
    navOffersBgInactive: COMMON.transparent,
    navOffersBorderActive: '#fb923c', // orange-400
    navOffersBorderInactive: '#d1d5db', // gray-300
    navReelsIcon: '#f91944',         // original primary color
    navReelsIconActive: COMMON.white,
    navReelsIconInactive: '#f91944', // primary when inactive (match Connect)
    navReelsBg: COMMON.transparent,
    navReelsBgActive: '#f91944',     // primary when active (match Connect)
    navReelsBgInactive: COMMON.transparent,
    navReelsBorderActive: '#f91944', // primary border when active (match Connect)
    navReelsBorderInactive: '#d1d5db', // light border when inactive (match Connect)
    navMapIcon: '#f91944',           // original primary color
    navMapIconActive: COMMON.white,
    navMapIconInactive: '#f91944',   // primary when inactive
    navMapBg: COMMON.transparent,
    navMapBgActive: '#f91944',       // primary when active
    navMapBgInactive: COMMON.transparent,
    navMapBorderActive: '#f91944',   // primary border when active
    navMapBorderInactive: '#d1d5db', // light border when inactive
    navConnectIcon: '#f91944',       // original primary color
    navConnectIconActive: COMMON.white,
    navConnectIconInactive: '#f91944', // primary when inactive
    navConnectBg: COMMON.transparent,
    navConnectBgActive: '#f91944',   // primary when active
    navConnectBgInactive: COMMON.transparent,
    navConnectBorderActive: '#f91944', // primary border when active
    navConnectBorderInactive: '#d1d5db', // light border when inactive
    navMenuBg: COMMON.white,
    navMenuBorder: COMMON.gray200,
    navMenuText: COMMON.gray700,
    
    // Cart Menu Order Type Selection
    cartOrderTypeSelectedBg: '#f91944',    // original primary (when selected)
    cartOrderTypeSelectedText: COMMON.white,
    cartOrderTypeSelectedBorder: '#f91944', // primary border
    cartOrderTypeUnselectedBg: COMMON.white,
    cartOrderTypeUnselectedText: COMMON.gray700,
    cartOrderTypeUnselectedBorder: COMMON.gray200,
    cartOrderTypeHoverBorder: '#f91944',   // primary on hover
    
    // Cart Increment/Decrement Buttons
    cartIncrementBg: '#f91944',           // original primary for increment buttons
    cartIncrementText: COMMON.white,
    cartIncrementBorder: '#f91944',       // primary border
    
    // Cart Total Amount
    cartTotalAmountColor: '#15803d',       // green-700 (dark green)
    
    // Category Selection
    categorySelectedTextBg: '#f91944',      // original primary (when selected)
    categorySelectedText: COMMON.white,
    categoryImageBorder: '#f91944',         // original primary for image border
    
    // Variant Prices
    variantPriceText: '#f91944',            // original primary for variant prices
    
    // Info Icon
    infoIconColor: 'rgba(156, 163, 175, 0.3)', // light grey with opacity for info icon in cherry theme
    infoIconBg: 'rgba(156, 163, 175, 0.1)',    // light grey with opacity for background
    
    // Location Header (Above Banner)
    locationBg: 'linear-gradient(to right, #dc2626, #ef4444, #dc2626)', // red gradient
    locationText: COMMON.white,
    locationIcon: COMMON.white,
    locationAccent: '#fbbf24',             // yellow accent dots
    
    // Back to Menu Button
    backButtonBg: COMMON.transparent,
    backButtonText: '#dc2626',         // red-600 text (dark red)
    backButtonBorder: '#fca5a5',       // red-300 border
    backButtonHoverBg: 'rgba(249, 25, 68, 0.1)', // subtle pink hover effect
    
    // All Categories Button
    allCategoriesBg: COMMON.transparent,
    allCategoriesText: COMMON.gray700,
    allCategoriesBorder: COMMON.gray200,
    allCategoriesTextBg: COMMON.transparent,
    
    // Menu Icon
    menuIconColor: '#B8BFC9FF',                // light grey color for menu icon
    
    // Navbar
    navbarBg: '#fcf4e0',  // light cream background on scroll
    navbarText: '#000000',  // black text
  }
};

// Pear Theme - Fresh Green Theme with Peach Accents
export const pearTheme = {
  name: 'pear',
  displayName: 'Pear',
  colors: {
    // Primary button colors - Green gradient
    buttonGradientFrom: '#10b981', // emerald-500
    buttonGradientTo: '#059669',   // emerald-600
    buttonText: COMMON.white,
    buttonRing: '#6ee7b7',         // emerald-300
    
    // Highlighted/Selected states
    highlightedBg: '#d1fae5',      // emerald-100
    highlightedBgGradientFrom: '#d1fae5', // emerald-100
    highlightedBgGradientTo: '#ecfdf5',   // emerald-50
    highlightedText: '#059669',    // emerald-600
    highlightedRipple: '#a7f3d0',  // emerald-200
    
    // Cart badge
    cartBadgeGradientFrom: '#10b981', // emerald-500
    cartBadgeGradientTo: '#059669',   // emerald-600
    cartBadgeText: COMMON.white,
    
    // Bottom tab navigation
    bottomTabBg: COMMON.white,
    bottomTabBorder: COMMON.gray200,
    bottomTabInactiveText: COMMON.gray600,
    bottomTabActiveText: '#059669',   // emerald-600
    bottomTabActiveBgFrom: '#d1fae5', // emerald-100
    bottomTabActiveBgTo: '#ecfdf5',   // emerald-50
    
    // Quantity controls
    quantityControlBg: '#059669',  // emerald-600
    quantityControlText: COMMON.white,
    
    // General UI
    primaryColor: '#10b981',       // emerald-500
    secondaryColor: '#059669',     // emerald-600
    accentColor: '#fbbf24',        // peach/amber accent
    backgroundColor: COMMON.white,
    surfaceColor: COMMON.graySurface,
    borderColor: COMMON.gray200,
    textPrimary: COMMON.gray900,
    textSecondary: COMMON.gray500,
    
    // Hover states
    hoverColor: '#059669',         // emerald-600
    
    // Offer Tags & Badges
    offerPriceText: '#059669',     // emerald-600
    badgeVeg: COMMON.vegGreen,
    badgeNonVeg: COMMON.nonVegRed,
    seasonalBadgeFrom: '#f59e0b',  // amber-500
    seasonalBadgeTo: '#d97706',    // amber-600
    featuredBadgeFrom: '#fde047',  // yellow-300
    featuredBadgeTo: '#eab308',    // yellow-500
    featuredBadgeText: '#713f12',  // yellow-900
    offerHeaderBg: '#10b981',      // emerald-500
    offerHeaderText: COMMON.white,
    
    // Search & Product Buttons
    searchButtonBg: '#10b981',     // emerald-500
    searchButtonText: COMMON.white,
    searchButtonBorder: COMMON.transparent,
    searchIconColor: '#10b981',    // emerald-500
    searchTextColor: '#10b981',    // emerald-500
    productAddButtonBg: '#10b981', // emerald-500
    productAddButtonText: COMMON.white,
    productAddButtonBorder: COMMON.transparent,
    
    // Floating Cart Icon
    cartIconGradientFrom: '#10b981', // emerald-500
    cartIconGradientVia: '#34d399',  // emerald-400
    cartIconGradientTo: '#fbbf24',   // amber-400 (peach accent)
    cartIconText: COMMON.white,
    cartIconBorder: COMMON.white,
    cartIconShadow: '0 4px 32px 0 rgba(16,185,129,0.18)',
    cartIconBadgeBg: '#059669',      // emerald-600 (better contrast)
    cartIconBadgeText: COMMON.white, // white text for visibility
    
    // Top Navigation Menu Options
    navOffersIconActive: '#d97706',  // amber-600
    navOffersIconInactive: '#f59e0b', // amber-500
    navOffersBgActive: '#fef3c7',    // amber-100
    navOffersBgInactive: COMMON.transparent,
    navOffersBorderActive: '#fbbf24', // amber-400
    navOffersBorderInactive: '#d1d5db', // gray-300
    navReelsIcon: '#10b981',         // emerald-500
    navReelsIconActive: COMMON.white,
    navReelsIconInactive: '#10b981',
    navReelsBg: COMMON.transparent,
    navReelsBgActive: '#10b981',
    navReelsBgInactive: COMMON.transparent,
    navReelsBorderActive: '#10b981',
    navReelsBorderInactive: '#d1d5db',
    navMapIcon: '#10b981',
    navMapIconActive: COMMON.white,
    navMapIconInactive: '#10b981',
    navMapBg: COMMON.transparent,
    navMapBgActive: '#10b981',
    navMapBgInactive: COMMON.transparent,
    navMapBorderActive: '#10b981',
    navMapBorderInactive: '#d1d5db',
    navConnectIcon: '#10b981',
    navConnectIconActive: COMMON.white,
    navConnectIconInactive: '#10b981',
    navConnectBg: COMMON.transparent,
    navConnectBgActive: '#10b981',
    navConnectBgInactive: COMMON.transparent,
    navConnectBorderActive: '#10b981',
    navConnectBorderInactive: '#d1d5db',
    navMenuBg: COMMON.white,
    navMenuBorder: COMMON.gray200,
    navMenuText: COMMON.gray700,
    
    // Cart Menu Order Type Selection
    cartOrderTypeSelectedBg: '#10b981',
    cartOrderTypeSelectedText: COMMON.white,
    cartOrderTypeSelectedBorder: '#10b981',
    cartOrderTypeUnselectedBg: COMMON.white,
    cartOrderTypeUnselectedText: COMMON.gray700,
    cartOrderTypeUnselectedBorder: COMMON.gray200,
    cartOrderTypeHoverBorder: '#10b981',
    
    // Cart Increment/Decrement Buttons
    cartIncrementBg: '#10b981',
    cartIncrementText: COMMON.white,
    cartIncrementBorder: '#10b981',
    
    // Cart Total Amount
    cartTotalAmountColor: '#15803d',       // green-700 (dark green)
    
    // Category Selection
    categorySelectedTextBg: '#10b981',
    categorySelectedText: COMMON.white,
    categoryImageBorder: '#10b981',
    
    // Variant Prices
    variantPriceText: '#10b981',
    
    // Info Icon
    infoIconColor: 'rgba(156, 163, 175, 0.3)',
    infoIconBg: 'rgba(156, 163, 175, 0.1)',
    
    // Location Header (Above Banner)
    locationBg: 'linear-gradient(to right, #059669, #10b981, #059669)',
    locationText: COMMON.white,
    locationIcon: COMMON.white,
    locationAccent: '#fbbf24',
    
    // Back to Menu Button
    backButtonBg: COMMON.transparent,
    backButtonText: '#059669',         // emerald-600 text (dark green)
    backButtonBorder: '#6ee7b7',       // emerald-300 border
    backButtonHoverBg: 'rgba(16, 185, 129, 0.1)', // subtle emerald hover effect
    
    // All Categories Button
    allCategoriesBg: COMMON.transparent,
    allCategoriesText: COMMON.gray700,
    allCategoriesBorder: COMMON.gray200,
    allCategoriesTextBg: COMMON.transparent,
    
    // Menu Icon
    menuIconColor: '#fbbf24',  // amber/peachy color for menu icon
    
    // Navbar
    navbarBg: '#fef3c7',  // light amber background on scroll
    navbarText: '#000000',  // black text
  }
};

// Citrus Theme - Vibrant Orange Theme
export const citrusTheme = {
  name: 'citrus',
  displayName: 'Citrus',
  colors: {
    // Primary button colors - Orange gradient
    buttonGradientFrom: '#f97316', // orange-500
    buttonGradientTo: '#ea580c',   // orange-600
    buttonText: COMMON.white,
    buttonRing: '#fdba74',         // orange-300
    
    // Highlighted/Selected states
    highlightedBg: '#ffedd5',      // orange-100
    highlightedBgGradientFrom: '#ffedd5', // orange-100
    highlightedBgGradientTo: '#fff7ed',   // orange-50
    highlightedText: '#ea580c',    // orange-600
    highlightedRipple: '#fed7aa',  // orange-200
    
    // Cart badge
    cartBadgeGradientFrom: '#f97316', // orange-500
    cartBadgeGradientTo: '#ea580c',   // orange-600
    cartBadgeText: COMMON.white,
    
    // Bottom tab navigation
    bottomTabBg: COMMON.white,
    bottomTabBorder: COMMON.gray200,
    bottomTabInactiveText: COMMON.gray600,
    bottomTabActiveText: '#ea580c',   // orange-600
    bottomTabActiveBgFrom: '#ffedd5', // orange-100
    bottomTabActiveBgTo: '#fff7ed',   // orange-50
    
    // Quantity controls
    quantityControlBg: '#ea580c',  // orange-600
    quantityControlText: COMMON.white,
    
    // General UI
    primaryColor: '#f97316',       // orange-500
    secondaryColor: '#ea580c',     // orange-600
    accentColor: '#facc15',        // yellow accent
    backgroundColor: COMMON.white,
    surfaceColor: COMMON.graySurface,
    borderColor: COMMON.gray200,
    textPrimary: COMMON.gray900,
    textSecondary: COMMON.gray500,
    
    // Hover states
    hoverColor: '#ea580c',         // orange-600
    
    // Offer Tags & Badges
    offerPriceText: '#ea580c',     // orange-600
    badgeVeg: COMMON.vegGreen,
    badgeNonVeg: COMMON.nonVegRed,
    seasonalBadgeFrom: '#fbbf24',  // amber-400
    seasonalBadgeTo: '#f59e0b',    // amber-500
    featuredBadgeFrom: '#fde047',  // yellow-300
    featuredBadgeTo: '#facc15',    // yellow-400
    featuredBadgeText: '#713f12',  // yellow-900
    offerHeaderBg: '#f97316',      // orange-500
    offerHeaderText: COMMON.white,
    
    // Search & Product Buttons
    searchButtonBg: '#f97316',     // orange-500
    searchButtonText: COMMON.white,
    searchButtonBorder: COMMON.transparent,
    searchIconColor: '#f97316',    // orange-500
    searchTextColor: '#f97316',    // orange-500
    productAddButtonBg: '#f97316', // orange-500
    productAddButtonText: COMMON.white,
    productAddButtonBorder: COMMON.transparent,
    
    // Floating Cart Icon
    cartIconGradientFrom: '#f97316', // orange-500
    cartIconGradientVia: '#fb923c',  // orange-400
    cartIconGradientTo: '#facc15',   // yellow-400 (citrus accent)
    cartIconText: COMMON.white,
    cartIconBorder: COMMON.white,
    cartIconShadow: '0 4px 32px 0 rgba(249,115,22,0.18)',
    cartIconBadgeBg: '#facc15',      // yellow-400
    cartIconBadgeText: '#1f2937',    // gray-800
    
    // Top Navigation Menu Options
    navOffersIconActive: '#c2410c',  // orange-700
    navOffersIconInactive: '#ea580c', // orange-600
    navOffersBgActive: '#fed7aa',    // orange-200
    navOffersBgInactive: COMMON.transparent,
    navOffersBorderActive: '#fb923c', // orange-400
    navOffersBorderInactive: '#d1d5db', // gray-300
    navReelsIcon: '#f97316',         // orange-500
    navReelsIconActive: COMMON.white,
    navReelsIconInactive: '#f97316',
    navReelsBg: COMMON.transparent,
    navReelsBgActive: '#f97316',
    navReelsBgInactive: COMMON.transparent,
    navReelsBorderActive: '#f97316',
    navReelsBorderInactive: '#d1d5db',
    navMapIcon: '#f97316',
    navMapIconActive: COMMON.white,
    navMapIconInactive: '#f97316',
    navMapBg: COMMON.transparent,
    navMapBgActive: '#f97316',
    navMapBgInactive: COMMON.transparent,
    navMapBorderActive: '#f97316',
    navMapBorderInactive: '#d1d5db',
    navConnectIcon: '#f97316',
    navConnectIconActive: COMMON.white,
    navConnectIconInactive: '#f97316',
    navConnectBg: COMMON.transparent,
    navConnectBgActive: '#f97316',
    navConnectBgInactive: COMMON.transparent,
    navConnectBorderActive: '#f97316',
    navConnectBorderInactive: '#d1d5db',
    navMenuBg: COMMON.white,
    navMenuBorder: COMMON.gray200,
    navMenuText: COMMON.gray700,
    
    // Cart Menu Order Type Selection
    cartOrderTypeSelectedBg: '#f97316',
    cartOrderTypeSelectedText: COMMON.white,
    cartOrderTypeSelectedBorder: '#f97316',
    cartOrderTypeUnselectedBg: COMMON.white,
    cartOrderTypeUnselectedText: COMMON.gray700,
    cartOrderTypeUnselectedBorder: COMMON.gray200,
    cartOrderTypeHoverBorder: '#f97316',
    
    // Cart Increment/Decrement Buttons
    cartIncrementBg: '#f97316',
    cartIncrementText: COMMON.white,
    cartIncrementBorder: '#f97316',
    
    // Cart Total Amount
    cartTotalAmountColor: '#15803d',       // green-700 (dark green)
    
    // Category Selection
    categorySelectedTextBg: '#f97316',
    categorySelectedText: COMMON.white,
    categoryImageBorder: '#f97316',
    
    // Variant Prices
    variantPriceText: '#f97316',
    
    // Info Icon
    infoIconColor: 'rgba(156, 163, 175, 0.3)',
    infoIconBg: 'rgba(156, 163, 175, 0.1)',
    
    // Location Header (Above Banner)
    locationBg: 'linear-gradient(to right, #ea580c, #f97316, #ea580c)',
    locationText: COMMON.white,
    locationIcon: COMMON.white,
    locationAccent: '#facc15',
    
    // Back to Menu Button
    backButtonBg: COMMON.transparent,
    backButtonText: '#ea580c',         // orange-600 text (dark orange)
    backButtonBorder: '#fdba74',       // orange-300 border
    backButtonHoverBg: 'rgba(249, 115, 22, 0.1)', // subtle orange hover effect
    
    // All Categories Button
    allCategoriesBg: COMMON.transparent,
    allCategoriesText: COMMON.gray700,
    allCategoriesBorder: COMMON.gray200,
    allCategoriesTextBg: COMMON.transparent,
    
    // Menu Icon
    menuIconColor: '#facc15',  // yellow/citrus color for menu icon
    
    // Navbar
    navbarBg: '#fef9c3',  // light yellow background on scroll
    navbarText: '#000000',  // black text
  }
};

// Fig Theme - Bright Pink Theme based on #ec4899
export const dragonTheme = {
  name: 'dragon',
  displayName: 'dragon',
  colors: {
    // Primary button colors - Bright pink gradient
    buttonGradientFrom: '#ec4899', // pink-500
    buttonGradientTo: '#db2777',   // pink-600
    buttonText: COMMON.white,
    buttonRing: '#f9a8d4',         // pink-300
    
    // Highlighted/Selected states
    highlightedBg: '#fce7f3',      // pink-100
    highlightedBgGradientFrom: '#fce7f3', // pink-100
    highlightedBgGradientTo: '#fdf2f8',   // pink-50
    highlightedText: '#ec4899',    // pink-500
    highlightedRipple: '#fbcfe8',  // pink-200
    
    // Cart badge
    cartBadgeGradientFrom: '#ec4899', // pink-500
    cartBadgeGradientTo: '#db2777',   // pink-600
    cartBadgeText: COMMON.white,
    
    // Bottom tab navigation
    bottomTabBg: COMMON.white,
    bottomTabBorder: COMMON.gray200,
    bottomTabInactiveText: COMMON.gray600,
    bottomTabActiveText: '#ec4899',   // pink-500
    bottomTabActiveBgFrom: '#fce7f3', // pink-100
    bottomTabActiveBgTo: '#fdf2f8',   // pink-50
    
    // Quantity controls
    quantityControlBg: '#ec4899',  // pink-500
    quantityControlText: COMMON.white,
    
    // General UI
    primaryColor: '#ec4899',       // pink-500 (main accent)
    secondaryColor: '#db2777',     // pink-600
    accentColor: '#f9a8d4',        // pink-300
    backgroundColor: COMMON.white,
    surfaceColor: COMMON.graySurface,
    borderColor: COMMON.gray200,
    textPrimary: COMMON.gray900,
    textSecondary: COMMON.gray500,
    
    // Hover states
    hoverColor: '#db2777',         // pink-600
    
    // Offer Tags & Badges
    offerPriceText: '#ec4899',     // pink-500
    badgeVeg: COMMON.vegGreen,
    badgeNonVeg: COMMON.nonVegRed,
    seasonalBadgeFrom: '#f472b6',  // pink-400
    seasonalBadgeTo: '#ec4899',    // pink-500
    featuredBadgeFrom: '#fbcfe8',  // pink-200
    featuredBadgeTo: '#f9a8d4',    // pink-300
    featuredBadgeText: '#831843',  // pink-900
    offerHeaderBg: '#ec4899',      // pink-500
    offerHeaderText: COMMON.white,
    
    // Search & Product Buttons
    searchButtonBg: '#ec4899',     // pink-500
    searchButtonText: COMMON.white,
    searchButtonBorder: COMMON.transparent,
    searchIconColor: '#ec4899',    // pink-500
    searchTextColor: '#ec4899',    // pink-500
    productAddButtonBg: '#ec4899', // pink-500
    productAddButtonText: COMMON.white,
    productAddButtonBorder: COMMON.transparent,
    
    // Floating Cart Icon
    cartIconGradientFrom: '#ec4899', // pink-500
    cartIconGradientVia: '#f472b6',  // pink-400
    cartIconGradientTo: '#f9a8d4',   // pink-300
    cartIconText: COMMON.white,
    cartIconBorder: COMMON.white,
    cartIconShadow: '0 4px 32px 0 rgba(236,72,153,0.18)',
    cartIconBadgeBg: '#f9a8d4',      // pink-300
    cartIconBadgeText: '#831843',    // pink-900
    
    // Top Navigation Menu Options
    navOffersIconActive: '#be185d',  // pink-700
    navOffersIconInactive: '#db2777', // pink-600
    navOffersBgActive: '#fce7f3',    // pink-100
    navOffersBgInactive: COMMON.transparent,
    navOffersBorderActive: '#f9a8d4', // pink-300
    navOffersBorderInactive: '#d1d5db',
    navReelsIcon: '#ec4899',         // pink-500
    navReelsIconActive: COMMON.white,
    navReelsIconInactive: '#ec4899',
    navReelsBg: COMMON.transparent,
    navReelsBgActive: '#ec4899',
    navReelsBgInactive: COMMON.transparent,
    navReelsBorderActive: '#ec4899',
    navReelsBorderInactive: '#d1d5db',
    navMapIcon: '#ec4899',
    navMapIconActive: COMMON.white,
    navMapIconInactive: '#ec4899',
    navMapBg: COMMON.transparent,
    navMapBgActive: '#ec4899',
    navMapBgInactive: COMMON.transparent,
    navMapBorderActive: '#ec4899',
    navMapBorderInactive: '#d1d5db',
    navConnectIcon: '#ec4899',
    navConnectIconActive: COMMON.white,
    navConnectIconInactive: '#ec4899',
    navConnectBg: COMMON.transparent,
    navConnectBgActive: '#ec4899',
    navConnectBgInactive: COMMON.transparent,
    navConnectBorderActive: '#ec4899',
    navConnectBorderInactive: '#d1d5db',
    navMenuBg: COMMON.white,
    navMenuBorder: COMMON.gray200,
    navMenuText: COMMON.gray700,
    
    // Cart Menu Order Type Selection
    cartOrderTypeSelectedBg: '#ec4899',
    cartOrderTypeSelectedText: COMMON.white,
    cartOrderTypeSelectedBorder: '#ec4899',
    cartOrderTypeUnselectedBg: COMMON.white,
    cartOrderTypeUnselectedText: COMMON.gray700,
    cartOrderTypeUnselectedBorder: COMMON.gray200,
    cartOrderTypeHoverBorder: '#ec4899',
    
    // Cart Increment/Decrement Buttons
    cartIncrementBg: '#ec4899',
    cartIncrementText: COMMON.white,
    cartIncrementBorder: '#ec4899',
    
    // Cart Total Amount
    cartTotalAmountColor: '#15803d',       // green-700 (dark green)
    
    // Category Selection
    categorySelectedTextBg: '#ec4899',
    categorySelectedText: COMMON.white,
    categoryImageBorder: '#ec4899',
    
    // Variant Prices
    variantPriceText: '#ec4899',
    
    // Info Icon
    infoIconColor: 'rgba(156, 163, 175, 0.3)',
    infoIconBg: 'rgba(156, 163, 175, 0.1)',
    
    // Location Header (Above Banner)
    locationBg: 'linear-gradient(to right, #db2777, #ec4899, #db2777)', // pink gradient
    locationText: COMMON.white,
    locationIcon: COMMON.white,
    locationAccent: '#f9a8d4',      // pink-300 accent
    
    // Back to Menu Button
    backButtonBg: COMMON.transparent,
    backButtonText: '#db2777',         // pink-600 text (dark pink)
    backButtonBorder: '#f9a8d4',       // pink-300 border
    backButtonHoverBg: 'rgba(236, 72, 153, 0.1)', // subtle pink hover
    
    // All Categories Button
    allCategoriesBg: COMMON.transparent,
    allCategoriesText: COMMON.gray700,
    allCategoriesBorder: COMMON.gray200,
    allCategoriesTextBg: COMMON.transparent,
    
    // Menu Icon
    menuIconColor: '#f9a8d4',  // pink-300 for menu icon
    
    // Navbar
    navbarBg: '#fce7f3',  // light pink background on scroll
    navbarText: '#000000',  // black text
  }
};

// Grape Theme - Purple Theme with Pink Buttons
export const figTheme = {
  name: 'fig',
  displayName: 'Fig',
  colors: {
    // Primary button colors - Pink gradient as specified
    buttonGradientFrom: '#ec4899', // pink-500
    buttonGradientTo: '#db2777',   // pink-600
    buttonText: COMMON.white,
    buttonRing: '#f9a8d4',         // pink-300
    
    // Highlighted/Selected states
    highlightedBg: '#f3e8ff',      // purple-100
    highlightedBgGradientFrom: '#f3e8ff', // purple-100
    highlightedBgGradientTo: '#faf5ff',   // purple-50
    highlightedText: '#9333ea',    // purple-600
    highlightedRipple: '#e9d5ff',  // purple-200
    
    // Cart badge
    cartBadgeGradientFrom: '#a855f7', // purple-500
    cartBadgeGradientTo: '#9333ea',   // purple-600
    cartBadgeText: COMMON.white,
    
    // Bottom tab navigation
    bottomTabBg: COMMON.white,
    bottomTabBorder: COMMON.gray200,
    bottomTabInactiveText: COMMON.gray600,
    bottomTabActiveText: '#9333ea',   // purple-600
    bottomTabActiveBgFrom: '#f3e8ff', // purple-100
    bottomTabActiveBgTo: '#faf5ff',   // purple-50
    
    // Quantity controls
    quantityControlBg: '#9333ea',  // purple-600
    quantityControlText: COMMON.white,
    
    // General UI
    primaryColor: '#a855f7',       // purple-500
    secondaryColor: '#9333ea',     // purple-600
    accentColor: '#ec4899',        // pink-500 accent
    backgroundColor: COMMON.white,
    surfaceColor: COMMON.graySurface,
    borderColor: COMMON.gray200,
    textPrimary: COMMON.gray900,
    textSecondary: COMMON.gray500,
    
    // Hover states
    hoverColor: '#9333ea',         // purple-600
    
    // Offer Tags & Badges
    offerPriceText: '#9333ea',     // purple-600
    badgeVeg: COMMON.vegGreen,
    badgeNonVeg: COMMON.nonVegRed,
    seasonalBadgeFrom: '#c084fc',  // purple-400
    seasonalBadgeTo: '#a855f7',    // purple-500
    featuredBadgeFrom: '#f0abfc',  // fuchsia-300
    featuredBadgeTo: '#e879f9',    // fuchsia-400
    featuredBadgeText: '#701a75',  // fuchsia-900
    offerHeaderBg: '#a855f7',      // purple-500
    offerHeaderText: COMMON.white,
    
    // Search & Product Buttons
    searchButtonBg: '#a855f7',     // purple-500
    searchButtonText: COMMON.white,
    searchButtonBorder: COMMON.transparent,
    searchIconColor: '#a855f7',    // purple-500
    searchTextColor: '#a855f7',    // purple-500
    productAddButtonBg: '#a855f7', // purple-500
    productAddButtonText: COMMON.white,
    productAddButtonBorder: COMMON.transparent,
    
    // Floating Cart Icon
    cartIconGradientFrom: '#a855f7', // purple-500
    cartIconGradientVia: '#c084fc',  // purple-400
    cartIconGradientTo: '#ec4899',   // pink-500 (fig accent)
    cartIconText: COMMON.white,
    cartIconBorder: COMMON.white,
    cartIconShadow: '0 4px 32px 0 rgba(168,85,247,0.18)',
    cartIconBadgeBg: '#ec4899',      // pink-500
    cartIconBadgeText: '#ffffff',    // white
    
    // Top Navigation Menu Options
    navOffersIconActive: '#7e22ce',  // purple-700
    navOffersIconInactive: '#9333ea', // purple-600
    navOffersBgActive: '#e9d5ff',    // purple-200
    navOffersBgInactive: COMMON.transparent,
    navOffersBorderActive: '#c084fc', // purple-400
    navOffersBorderInactive: '#d1d5db', // gray-300
    navReelsIcon: '#a855f7',         // purple-500
    navReelsIconActive: COMMON.white,
    navReelsIconInactive: '#a855f7',
    navReelsBg: COMMON.transparent,
    navReelsBgActive: '#a855f7',
    navReelsBgInactive: COMMON.transparent,
    navReelsBorderActive: '#a855f7',
    navReelsBorderInactive: '#d1d5db',
    navMapIcon: '#a855f7',
    navMapIconActive: COMMON.white,
    navMapIconInactive: '#a855f7',
    navMapBg: COMMON.transparent,
    navMapBgActive: '#a855f7',
    navMapBgInactive: COMMON.transparent,
    navMapBorderActive: '#a855f7',
    navMapBorderInactive: '#d1d5db',
    navConnectIcon: '#a855f7',
    navConnectIconActive: COMMON.white,
    navConnectIconInactive: '#a855f7',
    navConnectBg: COMMON.transparent,
    navConnectBgActive: '#a855f7',
    navConnectBgInactive: COMMON.transparent,
    navConnectBorderActive: '#a855f7',
    navConnectBorderInactive: '#d1d5db',
    navMenuBg: COMMON.white,
    navMenuBorder: COMMON.gray200,
    navMenuText: COMMON.gray700,
    
    // Cart Menu Order Type Selection
    cartOrderTypeSelectedBg: '#a855f7',
    cartOrderTypeSelectedText: COMMON.white,
    cartOrderTypeSelectedBorder: '#a855f7',
    cartOrderTypeUnselectedBg: COMMON.white,
    cartOrderTypeUnselectedText: COMMON.gray700,
    cartOrderTypeUnselectedBorder: COMMON.gray200,
    cartOrderTypeHoverBorder: '#a855f7',
    
    // Cart Increment/Decrement Buttons
    cartIncrementBg: '#a855f7',
    cartIncrementText: COMMON.white,
    cartIncrementBorder: '#a855f7',
    
    // Cart Total Amount
    cartTotalAmountColor: '#15803d',       // green-700 (dark green)
    
    // Category Selection
    categorySelectedTextBg: '#a855f7',
    categorySelectedText: COMMON.white,
    categoryImageBorder: '#a855f7',
    
    // Variant Prices
    variantPriceText: '#a855f7',
    
    // Info Icon
    infoIconColor: 'rgba(156, 163, 175, 0.3)',
    infoIconBg: 'rgba(156, 163, 175, 0.1)',
    
    // Location Header (Above Banner)
    locationBg: 'linear-gradient(to right, #9333ea, #a855f7, #9333ea)',
    locationText: COMMON.white,
    locationIcon: COMMON.white,
    locationAccent: '#ec4899',
    
    // Back to Menu Button
    backButtonBg: COMMON.transparent,
    backButtonText: '#9333ea',         // purple-600 text (dark purple)
    backButtonBorder: '#c084fc',       // purple-400 border
    backButtonHoverBg: 'rgba(168, 85, 247, 0.1)', // subtle purple hover effect
    
    // All Categories Button
    allCategoriesBg: COMMON.transparent,
    allCategoriesText: COMMON.gray700,
    allCategoriesBorder: COMMON.gray200,
    allCategoriesTextBg: COMMON.transparent,
    
    // Menu Icon
    menuIconColor: '#ec4899',  // pink color for menu icon
    
    // Navbar
    navbarBg: '#fce7f3',  // light pink background on scroll
    navbarText: '#000000',  // black text
  }
};

// longan Theme - Light Cream Theme
export const longanTheme = {
  name: 'longan',
  displayName: 'Longan',
  colors: {
    // Primary button colors - Warm brown gradient
    buttonGradientFrom: '#a68a6a', // warm brown
    buttonGradientTo: '#8b7355',   // darker brown
    buttonText: COMMON.white,
    buttonRing: '#d4c5b0',         // light beige
    
    // Highlighted/Selected states
    highlightedBg: '#f8f4ed',      // very light cream
    highlightedBgGradientFrom: '#f8f4ed', // very light cream
    highlightedBgGradientTo: '#fdfbf7',   // almost white cream
    highlightedText: '#8b7355',    // dark brown
    highlightedRipple: '#ede5dc',  // light beige
    
    // Cart badge
    cartBadgeGradientFrom: '#8b7355', // dark brown
    cartBadgeGradientTo: '#6b5744',   // darker brown
    cartBadgeText: COMMON.white,
    
    // Bottom tab navigation
    bottomTabBg: COMMON.white,
    bottomTabBorder: COMMON.gray200,
    bottomTabInactiveText: COMMON.gray600,
    bottomTabActiveText: '#8b7355',   // dark brown
    bottomTabActiveBgFrom: '#f8f4ed', // very light cream
    bottomTabActiveBgTo: '#fdfbf7',   // almost white cream
    
    // Quantity controls
    quantityControlBg: '#a68a6a',  // warm brown
    quantityControlText: COMMON.white,
    
    // General UI
    primaryColor: '#a68a6a',       // warm brown (main accent)
    secondaryColor: '#8b7355',     // dark brown
    accentColor: '#d4c5b0',        // light beige
    backgroundColor: COMMON.white,
    surfaceColor: '#fdfbf7',       // almost white cream
    borderColor: '#ede5dc',        // light beige
    textPrimary: COMMON.gray900,
    textSecondary: COMMON.gray500,
    
    // Hover states
    hoverColor: '#8b7355',         // dark brown
    
    // Offer Tags & Badges
    offerPriceText: '#8b7355',     // dark brown
    badgeVeg: COMMON.vegGreen,
    badgeNonVeg: COMMON.nonVegRed,
    seasonalBadgeFrom: '#c9a882',  // tan
    seasonalBadgeTo: '#a68a6a',    // warm brown
    featuredBadgeFrom: '#f8f4ed',  // very light cream
    featuredBadgeTo: '#ede5dc',    // light beige
    featuredBadgeText: '#5c4a3a',  // very dark brown
    offerHeaderBg: '#a68a6a',      // warm brown
    offerHeaderText: COMMON.white,
    
    // Search & Product Buttons
    searchButtonBg: '#a68a6a',     // warm brown
    searchButtonText: COMMON.white,
    searchButtonBorder: COMMON.transparent,
    searchIconColor: '#a68a6a',    // warm brown
    searchTextColor: '#a68a6a',    // warm brown
    productAddButtonBg: '#a68a6a', // warm brown
    productAddButtonText: COMMON.white,
    productAddButtonBorder: COMMON.transparent,
    
    // Floating Cart Icon
    cartIconGradientFrom: '#a68a6a', // warm brown
    cartIconGradientVia: '#c9a882',  // tan
    cartIconGradientTo: '#d4c5b0',   // light beige
    cartIconText: COMMON.white,
    cartIconBorder: COMMON.white,
    cartIconShadow: '0 4px 32px 0 rgba(166,138,106,0.18)',
    cartIconBadgeBg: '#d4c5b0',      // light beige
    cartIconBadgeText: '#3e3e3e',    // dark gray
    
    // Top Navigation Menu Options
    navOffersIconActive: '#6b5744',  // darker brown
    navOffersIconInactive: '#8b7355', // dark brown
    navOffersBgActive: '#f8f4ed',    // very light cream
    navOffersBgInactive: COMMON.transparent,
    navOffersBorderActive: '#d4c5b0', // light beige
    navOffersBorderInactive: '#d1d5db',
    navReelsIcon: '#a68a6a',         // warm brown
    navReelsIconActive: COMMON.white,
    navReelsIconInactive: '#a68a6a',
    navReelsBg: COMMON.transparent,
    navReelsBgActive: '#a68a6a',
    navReelsBgInactive: COMMON.transparent,
    navReelsBorderActive: '#a68a6a',
    navReelsBorderInactive: '#d1d5db',
    navMapIcon: '#a68a6a',
    navMapIconActive: COMMON.white,
    navMapIconInactive: '#a68a6a',
    navMapBg: COMMON.transparent,
    navMapBgActive: '#a68a6a',
    navMapBgInactive: COMMON.transparent,
    navMapBorderActive: '#a68a6a',
    navMapBorderInactive: '#d1d5db',
    navConnectIcon: '#a68a6a',
    navConnectIconActive: COMMON.white,
    navConnectIconInactive: '#a68a6a',
    navConnectBg: COMMON.transparent,
    navConnectBgActive: '#a68a6a',
    navConnectBgInactive: COMMON.transparent,
    navConnectBorderActive: '#a68a6a',
    navConnectBorderInactive: '#d1d5db',
    navMenuBg: COMMON.white,
    navMenuBorder: '#ede5dc',
    navMenuText: COMMON.gray700,
    
    // Cart Menu Order Type Selection
    cartOrderTypeSelectedBg: '#a68a6a',
    cartOrderTypeSelectedText: COMMON.white,
    cartOrderTypeSelectedBorder: '#a68a6a',
    cartOrderTypeUnselectedBg: COMMON.white,
    cartOrderTypeUnselectedText: COMMON.gray700,
    cartOrderTypeUnselectedBorder: '#ede5dc',
    cartOrderTypeHoverBorder: '#a68a6a',
    
    // Cart Increment/Decrement Buttons
    cartIncrementBg: '#a68a6a',
    cartIncrementText: COMMON.white,
    cartIncrementBorder: '#a68a6a',
    
    // Cart Total Amount
    cartTotalAmountColor: '#15803d',       // green-700 (dark green)
    
    // Category Selection
    categorySelectedTextBg: '#a68a6a',
    categorySelectedText: COMMON.white,
    categoryImageBorder: '#a68a6a',
    
    // Variant Prices
    variantPriceText: '#8b7355',
    
    // Info Icon
    infoIconColor: 'rgba(166, 138, 106, 0.3)',
    infoIconBg: 'rgba(166, 138, 106, 0.1)',
    
    // Location Header (Above Banner)
    locationBg: 'linear-gradient(to right, #8b7355, #a68a6a, #8b7355)', // brown gradient
    locationText: COMMON.white,
    locationIcon: COMMON.white,
    locationAccent: '#d4c5b0',      // light beige accent
    
    // Back to Menu Button
    backButtonBg: COMMON.transparent,
    backButtonText: '#8b7355',         // brown text (dark brown)
    backButtonBorder: '#d4c5b0',       // light beige border
    backButtonHoverBg: 'rgba(166, 138, 106, 0.1)', // subtle brown hover
    
    // All Categories Button
    allCategoriesBg: COMMON.transparent,
    allCategoriesText: COMMON.gray700,
    allCategoriesBorder: '#ede5dc',
    allCategoriesTextBg: COMMON.transparent,
    
    // Menu Icon
    menuIconColor: '#d4c5b0',  // light beige for menu icon
    
    // Navbar
    navbarBg: '#f5f1ed',  // light beige background on scroll
    navbarText: '#000000',  // black text
  }
};

// Berry Theme - Sky Blue Focused with Dark Navy Base
export const berryTheme = {
  name: 'berry',
  displayName: 'Berry',
  colors: {
    // Primary button colors - Sky blue focused with navy depth
    buttonGradientFrom: '#0ea5e9', // sky-500 (primary - sky blue)
    buttonGradientTo: '#0284c7',   // sky-600 (deeper sky)
    buttonText: COMMON.white,
    buttonRing: '#7dd3fc',         // sky-300
    
    // Highlighted/Selected states - sky blue dominant
    highlightedBg: '#e0f2fe',      // sky-100 (lighter)
    highlightedBgGradientFrom: '#e0f2fe', // sky-100
    highlightedBgGradientTo: '#f0f9ff',   // sky-50
    highlightedText: '#0284c7',    // sky-600 (brighter)
    highlightedRipple: '#bae6fd',  // sky-200
    
    // Cart badge - sky blue with navy depth
    cartBadgeGradientFrom: '#0284c7', // sky-600
    cartBadgeGradientTo: '#0369a1',   // sky-700
    cartBadgeText: COMMON.white,
    
    // Bottom tab navigation - sky blue active states
    bottomTabBg: COMMON.white,
    bottomTabBorder: COMMON.gray200,
    bottomTabInactiveText: COMMON.gray600,
    bottomTabActiveText: '#0284c7',   // sky-600 (brighter)
    bottomTabActiveBgFrom: '#e0f2fe', // sky-100 (lighter)
    bottomTabActiveBgTo: '#f0f9ff',   // sky-50
    
    // Quantity controls - sky blue
    quantityControlBg: '#0ea5e9',  // sky-500 (brighter)
    quantityControlText: COMMON.white,
    
    // General UI - sky blue focused
    primaryColor: '#0ea5e9',       // sky-500 (main - bright sky)
    secondaryColor: '#0284c7',     // sky-600 (secondary sky)
    accentColor: '#38bdf8',        // sky-400 (light accent)
    backgroundColor: COMMON.white,
    surfaceColor: COMMON.graySurface,
    borderColor: COMMON.gray200,
    textPrimary: COMMON.gray900,
    textSecondary: COMMON.gray500,
    
    // Hover states - slightly darker sky
    hoverColor: '#0284c7',         // sky-600
    
    // Offer Tags & Badges - sky blue tones
    offerPriceText: '#0284c7',     // sky-600
    badgeVeg: COMMON.vegGreen,
    badgeNonVeg: COMMON.nonVegRed,
    seasonalBadgeFrom: '#38bdf8',  // sky-400 (lighter)
    seasonalBadgeTo: '#0ea5e9',    // sky-500
    featuredBadgeFrom: '#7dd3fc',  // sky-300
    featuredBadgeTo: '#38bdf8',    // sky-400
    featuredBadgeText: '#1e3a8a',  // navy-900 (dark contrast)
    offerHeaderBg: '#0ea5e9',      // sky-500
    offerHeaderText: COMMON.white,
    
    // Search & Product Buttons - bright sky blue
    searchButtonBg: '#0ea5e9',     // sky-500
    searchButtonText: COMMON.white,
    searchButtonBorder: COMMON.transparent,
    searchIconColor: '#0ea5e9',    // sky-500
    searchTextColor: '#0ea5e9',    // sky-500
    productAddButtonBg: '#0ea5e9', // sky-500
    productAddButtonText: COMMON.white,
    productAddButtonBorder: COMMON.transparent,
    
    // Floating Cart Icon - sky blue gradient with navy base
    cartIconGradientFrom: '#0284c7', // sky-600
    cartIconGradientVia: '#0ea5e9',  // sky-500
    cartIconGradientTo: '#7dd3fc',   // sky-300 (bright end)
    cartIconText: COMMON.white,
    cartIconBorder: COMMON.white,
    cartIconShadow: '0 4px 32px 0 rgba(14,165,233,0.25)',
    cartIconBadgeBg: '#1e40af',      // navy-800 (dark contrast)
    cartIconBadgeText: COMMON.white,
    
    // Top Navigation Menu Options - sky blue focused
    navOffersIconActive: '#0369a1',  // sky-700
    navOffersIconInactive: '#0284c7', // sky-600
    navOffersBgActive: '#e0f2fe',    // sky-100
    navOffersBgInactive: COMMON.transparent,
    navOffersBorderActive: '#0ea5e9', // sky-500
    navOffersBorderInactive: '#d1d5db', // gray-300
    navReelsIcon: '#0ea5e9',         // sky-500
    navReelsIconActive: COMMON.white,
    navReelsIconInactive: '#0ea5e9',
    navReelsBg: COMMON.transparent,
    navReelsBgActive: '#0ea5e9',
    navReelsBgInactive: COMMON.transparent,
    navReelsBorderActive: '#0ea5e9',
    navReelsBorderInactive: '#d1d5db',
    navMapIcon: '#0ea5e9',
    navMapIconActive: COMMON.white,
    navMapIconInactive: '#0ea5e9',
    navMapBg: COMMON.transparent,
    navMapBgActive: '#0ea5e9',
    navMapBgInactive: COMMON.transparent,
    navMapBorderActive: '#0ea5e9',
    navMapBorderInactive: '#d1d5db',
    navConnectIcon: '#0ea5e9',
    navConnectIconActive: COMMON.white,
    navConnectIconInactive: '#0ea5e9',
    navConnectBg: COMMON.transparent,
    navConnectBgActive: '#0ea5e9',
    navConnectBgInactive: COMMON.transparent,
    navConnectBorderActive: '#0ea5e9',
    navConnectBorderInactive: '#d1d5db',
    navMenuBg: COMMON.white,
    navMenuBorder: COMMON.gray200,
    navMenuText: COMMON.gray700,
    
    // Cart Menu Order Type Selection - sky blue
    cartOrderTypeSelectedBg: '#0ea5e9',
    cartOrderTypeSelectedText: COMMON.white,
    cartOrderTypeSelectedBorder: '#0ea5e9',
    cartOrderTypeUnselectedBg: COMMON.white,
    cartOrderTypeUnselectedText: COMMON.gray700,
    cartOrderTypeUnselectedBorder: COMMON.gray200,
    cartOrderTypeHoverBorder: '#0ea5e9',
    
    // Cart Increment/Decrement Buttons - sky blue
    cartIncrementBg: '#0ea5e9',
    cartIncrementText: COMMON.white,
    cartIncrementBorder: '#0ea5e9',
    
    // Cart Total Amount
    cartTotalAmountColor: '#15803d',       // green-700 (dark green)
    
    // Category Selection - sky blue
    categorySelectedTextBg: '#0ea5e9',
    categorySelectedText: COMMON.white,
    categoryImageBorder: '#0ea5e9',
    
    // Variant Prices - sky blue
    variantPriceText: '#0ea5e9',
    
    // Info Icon
    infoIconColor: 'rgba(14, 165, 233, 0.3)', // sky blue tint
    infoIconBg: 'rgba(14, 165, 233, 0.1)',    // sky blue tint
    
    // Location Header (Above Banner) - sky focused gradient with navy depth
    locationBg: 'linear-gradient(to right, #0369a1, #0ea5e9, #0369a1)', // sky gradient
    locationText: COMMON.white,
    locationIcon: COMMON.white,
    locationAccent: '#7dd3fc',     // bright sky accent
    
    // Back to Menu Button
    backButtonBg: COMMON.transparent,
    backButtonText: '#0369a1',         // sky-700 text (dark sky blue)
    backButtonBorder: '#7dd3fc',       // sky-300 border
    backButtonHoverBg: 'rgba(14, 165, 233, 0.1)', // sky blue hover
    
    // All Categories Button
    allCategoriesBg: COMMON.transparent,
    allCategoriesText: COMMON.gray700,
    allCategoriesBorder: COMMON.gray200,
    allCategoriesTextBg: COMMON.transparent,
    
    // Menu Icon
    menuIconColor: '#38bdf8',  // bright sky-400 for menu icon
    
    // Navbar
    navbarBg: '#e0f2fe',  // light sky background on scroll
    navbarText: '#000000',  // black text
  }
};

// palm Theme - Pure Black Theme
export const palmTheme = {
  name: 'palm',
  displayName: 'palm',
  colors: {
    // Primary button colors - Black button with white text
    buttonGradientFrom: '#000000', // black
    buttonGradientTo: '#1f1f1f',   // very dark gray
    buttonText: COMMON.white,         // white text on black button
    buttonRing: '#404040',         // dark gray
    
    // Highlighted/Selected states
    highlightedBg: '#1f1f1f',      // very dark gray
    highlightedBgGradientFrom: '#1f1f1f', // very dark gray
    highlightedBgGradientTo: '#0a0a0a',   // almost black
    highlightedText: '#ffffff',    // white
    highlightedRipple: '#404040',  // dark gray
    
    // Cart badge
    cartBadgeGradientFrom: '#000000', // black
    cartBadgeGradientTo: '#1f1f1f',   // very dark gray
    cartBadgeText: COMMON.white,      // white text
    
    // Bottom tab navigation
    bottomTabBg: COMMON.white,        // white
    bottomTabBorder: COMMON.gray200,
    bottomTabInactiveText: '#6b7280', // gray-500
    bottomTabActiveText: '#000000',   // black
    bottomTabActiveBgFrom: '#f3f4f6', // gray-100
    bottomTabActiveBgTo: '#e5e7eb',   // gray-200
    
    // Quantity controls
    quantityControlBg: '#000000',  // black buttons
    quantityControlText: COMMON.white, // white text
    
    // General UI
    primaryColor: '#ffffff',       // white
    secondaryColor: '#e5e7eb',     // gray-200
    accentColor: '#ffffff',        // white
    backgroundColor: '#000000',    // pure black
    surfaceColor: '#0a0a0a',       // almost black
    borderColor: '#262626',        // very dark gray
    textPrimary: '#ffffff',        // white
    textSecondary: '#9ca3af',      // gray-400
    
    // Hover states
    hoverColor: '#e5e7eb',         // light gray
    
    // Offer Tags & Badges (Dark theme variants - darker/muted)
    offerPriceText: '#15803d',     // green-700 (darker green)
    badgeVeg: '#166534',           // green-800 (dark green)
    badgeNonVeg: '#991b1b',        // red-800 (dark red)
    seasonalBadgeFrom: '#c2410c',  // orange-700 (darker orange)
    seasonalBadgeTo: '#991b1b',    // red-800 (darker red)
    featuredBadgeFrom: '#c0c0c0',  // silver gradient start
    featuredBadgeTo: '#e5e7eb',    // silver gradient end (light gray)
    featuredBadgeText: '#000000',  // black text
    offerHeaderBg: '#c2410c',      // orange-700 (darker orange)
    offerHeaderText: COMMON.white,    // white
    
    // Search & Product Buttons
    searchButtonBg: '#000000',     // black
    searchButtonText: COMMON.white,   // white
    searchButtonBorder: '#404040', // dark gray
    searchIconColor: '#6b7280',    // gray-500 (dark gray for visibility)
    searchTextColor: '#6b7280',    // gray-500 (dark gray for visibility)
    productAddButtonBg: '#000000', // black
    productAddButtonText: COMMON.white, // white
    productAddButtonBorder: '#404040', // dark gray
    
    // Floating Cart Icon - Dark gradient (increased visibility)
    cartIconGradientFrom: '#000000', // pure black
    cartIconGradientVia: '#4b5563',  // gray-600 (mid gray)
    cartIconGradientTo: '#9ca3af',   // gray-400 (lighter gray)
    cartIconText: COMMON.white,         // white
    cartIconBorder: '#9ca3af',       // gray-400
    cartIconShadow: '0 4px 32px 0 rgba(156,163,175,0.5)',
    cartIconBadgeBg: '#ffffff',      // white
    cartIconBadgeText: '#000000',    // black
    
    // Top Navigation Menu Options
    navOffersIconActive: '#f97316',  // orange-500 (when selected)
    navOffersIconInactive: '#f97316', // orange-500 (when not selected)
    navOffersBgActive: '#000000',    // black when selected
    navOffersBgInactive: COMMON.transparent,  // transparent when not selected
    navOffersBorderActive: '#404040', // dark gray border when selected
    navOffersBorderInactive: '#e5e7eb', // light gray border when not selected
    navReelsIconActive: COMMON.white,   // white when selected
    navReelsIconInactive: '#000000', // black when not selected
    navReelsBgActive: '#000000',     // black when selected
    navReelsBgInactive: COMMON.transparent, // transparent when not selected
    navReelsBorderActive: '#404040', // dark gray border when selected
    navReelsBorderInactive: '#e5e7eb', // light gray border when not selected
    navMapIconActive: COMMON.white,     // white when selected
    navMapIconInactive: '#16a34a',   // green-600 when not selected
    navMapBgActive: '#000000',       // black when selected
    navMapBgInactive: COMMON.transparent, // transparent when not selected
    navMapBorderActive: '#404040',   // dark gray border when selected
    navMapBorderInactive: '#e5e7eb', // light gray border when not selected
    navConnectIconActive: COMMON.white, // white when selected
    navConnectIconInactive: '#000000', // black when not selected
    navConnectBgActive: '#000000',   // black when selected
    navConnectBgInactive: COMMON.transparent, // transparent when not selected
    navConnectBorderActive: '#404040', // dark gray border when selected
    navConnectBorderInactive: '#e5e7eb', // light gray border when not selected
    navMenuBorder: COMMON.gray200,        // light gray
    navMenuText: '#000000',          // black
    
    // Cart Menu Order Type Selection
    cartOrderTypeSelectedBg: '#000000',    // black when selected
    cartOrderTypeSelectedText: COMMON.white,  // white text
    cartOrderTypeSelectedBorder: '#404040', // dark gray border
    cartOrderTypeUnselectedBg: '#fefbeb',  // light cream background when not selected
    cartOrderTypeUnselectedText: COMMON.gray700, // gray-700
    cartOrderTypeUnselectedBorder: COMMON.gray200, // light gray border
    cartOrderTypeHoverBorder: '#6b7280',   // gray-500
    
    // Cart Increment/Decrement Buttons
    cartIncrementBg: '#000000',            // black for increment buttons
    cartIncrementText: COMMON.white,          // white text
    cartIncrementBorder: '#404040',        // dark gray border
    
    // Cart Total Amount
    cartTotalAmountColor: '#15803d',       // green-700 (dark green)
    
    // Category Selection
    categorySelectedTextBg: '#fcf4e0',      // light cream color (when selected)
    categorySelectedText: '#000000',        // black text for selected category in dark theme
    categoryImageBorder: '#fcf4e0',        // light cream for image border
    
    // Variant Prices
    variantPriceText: '#6b7280',            // gray-500 (dark gray for visibility in dark theme)
    
    // Info Icon
    infoIconColor: '#000000',               // black for info icon in dark theme
    infoIconBg: 'rgba(255, 255, 255, 0.9)', // white with opacity for background
    
    // Location Header (Above Banner)
    locationBg: 'linear-gradient(to right, #374151, #4b5563, #374151)', // dark gray gradient
    locationText: COMMON.white,                // white text
    locationIcon: COMMON.white,                // white icon
    locationAccent: '#fbbf24',             // yellow accent dots (keep for visibility)
    
    // Back to Menu Button
    backButtonBg: COMMON.transparent,
    backButtonText: '#000000',             // black text
    backButtonBorder: '#6b7280',           // gray-500 border
    backButtonHoverBg: 'rgba(255, 255, 255, 0.1)', // subtle hover effect
    
    // All Categories Button
    allCategoriesBg: COMMON.transparent,          // transparent background
    allCategoriesText: '#ffffff',            // white text for dark theme
    allCategoriesBorder: '#d1d5db',          // gray-300 border (lighter for dark theme)
    allCategoriesTextBg: 'linear-gradient(135deg, #c0c0c0, #e5e7eb, #c0c0c0)', // silver gradient for dark theme
    
    // Menu Icon
    menuIconColor: '#B8BFC9FF',              // light grey for menu icon (same as cherry theme)
    
    // Navbar
    navbarBg: '#f3f4f5',  // light grey background on scroll
    navbarText: '#000000',  // black text
  }
};

// templates_1 Theme - Olive Green and Beige
export const templates1Theme = {
  name: 'templates_1',
  displayName: 'Templates 1',
  colors: {
    // Primary button colors - Dark Olive Green
    buttonGradientFrom: '#2f3f22',
    buttonGradientTo: '#1f2a17',
    buttonText: COMMON.white,
    buttonRing: '#e3cca3',
    
    // Highlighted/Selected states
    highlightedBg: '#e3cca3',
    highlightedBgGradientFrom: '#e3cca3',
    highlightedBgGradientTo: '#d5bd92',
    highlightedText: '#2f3f22',
    highlightedRipple: '#d5bd92',
    
    // Cart badge
    cartBadgeGradientFrom: '#2f3f22',
    cartBadgeGradientTo: '#1f2a17',
    cartBadgeText: COMMON.white,
    
    // Bottom tab navigation
    bottomTabBg: COMMON.white,
    bottomTabBorder: COMMON.gray200,
    bottomTabInactiveText: COMMON.gray600,
    bottomTabActiveText: '#2f3f22',
    bottomTabActiveBgFrom: '#e3cca3',
    bottomTabActiveBgTo: '#d5bd92',
    
    // Quantity controls
    quantityControlBg: '#2f3f22',
    quantityControlText: COMMON.white,
    
    // General UI
    primaryColor: '#2f3f22',
    secondaryColor: '#e3cca3',
    accentColor: '#e3cca3',
    backgroundColor: COMMON.white,
    surfaceColor: COMMON.graySurface,
    borderColor: COMMON.gray200,
    textPrimary: COMMON.gray900,
    textSecondary: COMMON.gray500,
    
    // Hover states
    hoverColor: '#1f2a17',
    
    // Offer Tags & Badges
    offerPriceText: '#2f3f22',
    badgeVeg: COMMON.vegGreen,
    badgeNonVeg: COMMON.nonVegRed,
    seasonalBadgeFrom: '#e3cca3',
    seasonalBadgeTo: '#d5bd92',
    featuredBadgeFrom: '#e3cca3',
    featuredBadgeTo: '#d5bd92',
    featuredBadgeText: '#2f3f22',
    offerHeaderBg: '#2f3f22',
    offerHeaderText: COMMON.white,
    
    // Search & Product Buttons
    searchButtonBg: '#2f3f22',
    searchButtonText: COMMON.white,
    searchButtonBorder: COMMON.transparent,
    searchIconColor: '#2f3f22',
    searchTextColor: '#2f3f22',
    productAddButtonBg: '#2f3f22',
    productAddButtonText: COMMON.white,
    productAddButtonBorder: COMMON.transparent,
    
    // Floating Cart Icon
    cartIconGradientFrom: '#2f3f22',
    cartIconGradientVia: '#2f3f22',
    cartIconGradientTo: '#1f2a17',
    cartIconText: COMMON.white,
    cartIconBorder: '#e3cca3',
    cartIconShadow: '0 4px 32px 0 rgba(47,63,34,0.3)',
    cartIconBadgeBg: '#e3cca3',
    cartIconBadgeText: '#2f3f22',
    
    // Top Navigation Menu Options
    navOffersIconActive: '#1f2a17',
    navOffersIconInactive: '#2f3f22',
    navOffersBgActive: '#e3cca3',
    navOffersBgInactive: COMMON.transparent,
    navOffersBorderActive: '#e3cca3',
    navOffersBorderInactive: '#d1d5db',
    navReelsIcon: '#2f3f22',
    navReelsIconActive: COMMON.white,
    navReelsIconInactive: '#2f3f22',
    navReelsBg: COMMON.transparent,
    navReelsBgActive: '#2f3f22',
    navReelsBgInactive: COMMON.transparent,
    navReelsBorderActive: '#2f3f22',
    navReelsBorderInactive: '#d1d5db',
    navMapIcon: '#2f3f22',
    navMapIconActive: COMMON.white,
    navMapIconInactive: '#2f3f22',
    navMapBg: COMMON.transparent,
    navMapBgActive: '#2f3f22',
    navMapBgInactive: COMMON.transparent,
    navMapBorderActive: '#2f3f22',
    navMapBorderInactive: '#d1d5db',
    navConnectIcon: '#2f3f22',
    navConnectIconActive: COMMON.white,
    navConnectIconInactive: '#2f3f22',
    navConnectBg: COMMON.transparent,
    navConnectBgActive: '#2f3f22',
    navConnectBgInactive: COMMON.transparent,
    navConnectBorderActive: '#2f3f22',
    navConnectBorderInactive: '#d1d5db',
    navMenuBg: COMMON.white,
    navMenuBorder: COMMON.gray200,
    navMenuText: COMMON.gray700,
    
    // Cart Menu Order Type Selection
    cartOrderTypeSelectedBg: '#2f3f22',
    cartOrderTypeSelectedText: COMMON.white,
    cartOrderTypeSelectedBorder: '#2f3f22',
    cartOrderTypeUnselectedBg: COMMON.white,
    cartOrderTypeUnselectedText: COMMON.gray700,
    cartOrderTypeUnselectedBorder: COMMON.gray200,
    cartOrderTypeHoverBorder: '#2f3f22',
    
    // Cart Increment/Decrement Buttons
    cartIncrementBg: '#2f3f22',
    cartIncrementText: COMMON.white,
    cartIncrementBorder: '#2f3f22',
    
    // Cart Total Amount
    cartTotalAmountColor: '#2f3f22',
    
    // Category Selection
    categorySelectedTextBg: '#e3cca3',
    categorySelectedText: '#2f3f22',
    categoryImageBorder: '#2f3f22',
    
    // Variant Prices
    variantPriceText: '#2f3f22',
    
    // Info Icon
    infoIconColor: 'rgba(47, 63, 34, 0.3)',
    infoIconBg: 'rgba(47, 63, 34, 0.1)',
    
    // Location Header (Above Banner)
    locationBg: 'linear-gradient(to right, #1f2a17, #2f3f22, #1f2a17)',
    locationText: COMMON.white,
    locationIcon: COMMON.white,
    locationAccent: '#e3cca3',
    
    // Back to Menu Button
    backButtonBg: COMMON.transparent,
    backButtonText: '#2f3f22',
    backButtonBorder: '#e3cca3',
    backButtonHoverBg: 'rgba(47, 63, 34, 0.1)',
    
    // All Categories Button
    allCategoriesBg: COMMON.transparent,
    allCategoriesText: COMMON.gray700,
    allCategoriesBorder: COMMON.gray200,
    allCategoriesTextBg: COMMON.transparent,
    
    // Menu Icon
    menuIconColor: '#2f3f22',
    
    // Navbar
    navbarBg: '#e3cca3',
    navbarText: '#2f3f22',
  }
};

// Theme registry - Frozen for performance
export const themes = Object.freeze({
  cherry: cherryTheme,
  pear: pearTheme,
  citrus: citrusTheme,
  dragon: dragonTheme,
  fig: figTheme,
  longan: longanTheme,
  berry: berryTheme,
  palm: palmTheme,
  templates_1: templates1Theme,
});

// Get theme by name with fallback to cherry
export const getTheme = (themeName) => {
  if (!themeName) return cherryTheme;
  
  const normalizedName = themeName.toLowerCase();
  return themes[normalizedName] || cherryTheme;
};

// Get theme from profile data - Optimized
export const getThemeFromProfile = (profile) => {
  return getTheme(profile?.theme?.themeName);
};

// Optimized helper functions using object mapping for better performance
const BUTTON_BASE = "flex items-center space-x-3 px-10 py-4 text-white poppins rounded-lg shadow-lg focus:outline-none focus:ring-4 transform transition duration-300 text-lg font-bold";

const BUTTON_THEME_MAP = {
  cherry: { classes: "bg-gradient-to-r from-red-500 to-pink-500 ring-red-300", hover: "hover:brightness-110 hover:scale-105" },
  pear: { classes: "bg-gradient-to-r from-emerald-500 to-emerald-600 ring-emerald-300", hover: "hover:brightness-110 hover:scale-105" },
  citrus: { classes: "bg-gradient-to-r from-orange-500 to-orange-600 ring-orange-300", hover: "hover:brightness-110 hover:scale-105" },
  dragon: { classes: "bg-gradient-to-r from-pink-500 to-pink-600 ring-pink-300 text-white", hover: "hover:brightness-110 hover:scale-105" },
  fig: { classes: "bg-gradient-to-r from-pink-500 to-pink-600 ring-pink-300", hover: "hover:brightness-110 hover:scale-105" },
  longan: { classes: "bg-gradient-to-r from-yellow-700 to-yellow-800 ring-yellow-500 text-white", hover: "hover:brightness-110 hover:scale-105" },
  berry: { classes: "bg-gradient-to-r from-sky-500 to-sky-600 ring-sky-300", hover: "hover:brightness-110 hover:scale-105" },
  palm: { classes: "bg-gradient-to-r from-black to-gray-800 ring-gray-700 border border-gray-700", hover: "hover:brightness-125 hover:scale-105" },
};

export const getButtonClasses = (theme, disabled = false) => {
  const config = BUTTON_THEME_MAP[theme.name] || BUTTON_THEME_MAP.cherry;
  const disabledClass = disabled ? "opacity-50 cursor-not-allowed" : config.hover;
  return `${BUTTON_BASE} ${config.classes} ${disabledClass}`.trim();
};

const BOTTOM_TAB_ACTIVE_MAP = {
  cherry: 'bg-gradient-to-br from-pink-100 to-pink-50',
  pear: 'bg-gradient-to-br from-emerald-100 to-emerald-50',
  citrus: 'bg-gradient-to-br from-orange-100 to-orange-50',
  fig: 'bg-gradient-to-br from-purple-100 to-purple-50',
  longan: 'bg-gradient-to-br from-yellow-100 to-yellow-50',
  berry: 'bg-gradient-to-br from-sky-100 to-sky-50',
  palm: 'bg-gradient-to-br from-gray-100 to-gray-200',
};

export const getBottomTabActiveClasses = (theme) => {
  const bgClass = BOTTOM_TAB_ACTIVE_MAP[theme.name] || '';
  return `${bgClass} shadow-lg scale-110 -translate-y-1`.trim();
};

const BOTTOM_TAB_INACTIVE_MAP = {
  dragon: 'text-gray-500 hover:scale-105 active:scale-95',
  longan: 'text-gray-500 hover:scale-105 active:scale-95',
  palm: 'text-gray-500 hover:text-black hover:scale-105 active:scale-95',
};

export const getBottomTabInactiveClasses = (theme) => {
  return BOTTOM_TAB_INACTIVE_MAP[theme.name] || 'text-gray-600 hover:scale-105 active:scale-95';
};

const BOTTOM_TAB_RIPPLE_MAP = {
  cherry: 'bg-pink-200/50',
  pear: 'bg-emerald-200/50',
  citrus: 'bg-orange-200/50',
  dragon: 'bg-pink-200/50',
  fig: 'bg-purple-200/50',
  longan: 'bg-yellow-200/50',
  berry: 'bg-sky-200/50',
  palm: 'bg-gray-300/50',
};

export const getBottomTabRippleClasses = (theme, isActive) => {
  if (!isActive) return '';
  const bgClass = BOTTOM_TAB_RIPPLE_MAP[theme.name] || '';
  return bgClass ? `${bgClass} animate-ping` : 'animate-ping';
};

export const getBottomTabBgClasses = (theme) => {
  return (theme.name === 'fig' || theme.name === 'longan') ? 'bg-white' : 'bg-white border-gray-200';
};

const CART_BADGE_MAP = {
  cherry: 'bg-gradient-to-br from-red-600 to-red-700',
  pear: 'bg-gradient-to-br from-emerald-800 to-emerald-900',
  citrus: 'bg-gradient-to-br from-orange-700 to-orange-800',
  dragon: 'bg-gradient-to-br from-pink-600 to-pink-700',
  fig: 'bg-gradient-to-br from-purple-700 to-purple-800',
  longan: 'bg-gradient-to-br from-yellow-700 to-yellow-800',
  berry: 'bg-gradient-to-br from-sky-600 to-sky-700',
  palm: 'bg-gradient-to-br from-black to-gray-900 border border-gray-700',
};

export const getCartBadgeClasses = (theme) => {
  const bgClass = CART_BADGE_MAP[theme.name] || '';
  return `${bgClass} text-white`.trim();
};

const QUANTITY_CONTROL_BASE = 'text-2xl w-8 h-8 rounded-full text-white hover:scale-105 transform transition duration-500 cursor-pointer p-1';

const QUANTITY_CONTROL_MAP = {
  cherry: 'bg-primary',
  pear: 'bg-emerald-500',
  citrus: 'bg-orange-500',
  dragon: 'bg-pink-500',
  fig: 'bg-purple-500',
  longan: 'bg-yellow-700',
  berry: 'bg-sky-500',
  palm: 'bg-black border border-gray-700',
};

export const getQuantityControlClasses = (theme, disabled = false) => {
  const bgClass = QUANTITY_CONTROL_MAP[theme.name] || '';
  const disabledClass = disabled ? ' opacity-50 cursor-not-allowed' : '';
  return `${QUANTITY_CONTROL_BASE} ${bgClass}${disabledClass}`.trim();
};

// Export default theme
export default cherryTheme;

